import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../atoms/Button';
import { DraftSkeletonCard } from './DraftSkeletonCard';
import { AnimatedDots } from './AnimatedDots';
import { useRotatingMessage } from '../../chat/draft/useRotatingMessage';
import {
  DRAFT_MESSAGES,
  INSTANT_DRAFT_MESSAGES,
  PRO_TIPS,
  QUESTIONS_MESSAGES,
  TEMPLATE_MESSAGES,
} from '../../chat/draft/draftLoadingMessages';
import type { DraftPhase } from '../../chat/draft/types';

/**
 * Frontend-prod draft loader, adapted for the 360px task pane.
 *
 * SOURCE: frontend-prod/features/draft/DraftCreationLoader.tsx (338 LOC)
 * Web layout is full-page (centered card, ~210px wide, body LOC indicating
 * sections + pro-tip below). Plugin version stacks card + message vertically
 * inside a 360px absolute overlay; pro-tip uses an AnimatePresence fade like
 * web does.
 */

function messagesForPhase(phase: DraftPhase): readonly string[] {
  switch (phase) {
    case 'template_loading':
      return TEMPLATE_MESSAGES;
    case 'questions_loading':
      return QUESTIONS_MESSAGES;
    case 'instant_loading':
      return INSTANT_DRAFT_MESSAGES;
    case 'draft_loading':
    default:
      return DRAFT_MESSAGES;
  }
}

function staticLabelForPhase(phase: DraftPhase): string {
  switch (phase) {
    case 'template_loading':
      return 'Generating template';
    case 'questions_loading':
      return 'Preparing questions';
    case 'instant_loading':
      return 'Drafting (no questions asked)';
    case 'draft_loading':
    default:
      return 'Generating draft';
  }
}

export interface DraftPhaseLoaderProps {
  phase: DraftPhase;
  onStop?: () => void;
}

export function DraftPhaseLoader({ phase, onStop }: DraftPhaseLoaderProps): JSX.Element {
  const messages = messagesForPhase(phase);
  const active = useRotatingMessage(messages, 2400);
  const proTip = useRotatingMessage(PRO_TIPS, 8000);
  const staticLabel = staticLabelForPhase(phase);

  return (
    <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-background/95 backdrop-blur-sm px-4">
      <div className="flex flex-col items-center gap-5 w-full max-w-xs">
        {/* Phase pill */}
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
          {staticLabel}
        </div>

        {/* Animated draft skeleton card */}
        <DraftSkeletonCard />

        {/* Rotating status line */}
        <div
          className="relative h-6 overflow-hidden flex items-center justify-center w-full"
          aria-live="polite"
        >
          <AnimatePresence mode="wait">
            <motion.span
              key={active}
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -10, opacity: 0 }}
              transition={{ duration: 0.4, ease: 'easeInOut' }}
              className="italic text-sm text-foreground inline-flex items-center"
            >
              {active}
              <AnimatedDots />
            </motion.span>
          </AnimatePresence>
        </div>

        {/* Pro tip with fade */}
        <div className="relative min-h-[2.25rem] w-full text-center">
          <AnimatePresence mode="wait">
            <motion.p
              key={proTip}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.4, ease: 'easeInOut' }}
              className="text-[11px] text-muted-foreground leading-relaxed px-2"
            >
              <span className="font-semibold text-foreground/80">Pro tip:</span> {proTip}
            </motion.p>
          </AnimatePresence>
        </div>

        {/* Shimmer progress bar (sourced from web's bottom shimmer) */}
        <div className="relative w-32 h-[2px] overflow-hidden rounded-full bg-primary/15">
          <motion.span
            className="absolute inset-y-0 w-1/3 rounded-full bg-gradient-to-r from-transparent via-primary to-transparent"
            animate={{ x: ['-100%', '300%'] }}
            transition={{ repeat: Infinity, duration: 1.8, ease: 'easeInOut' }}
          />
        </div>

        {onStop && (
          <Button variant="outline" size="sm" className="mt-1" onClick={onStop}>
            Stop
          </Button>
        )}
      </div>
    </div>
  );
}
